/**
 * Phase 17 Plan 17-02 — CreateKpiTemplateDialog
 *
 * Admin-only dialog to create a new KPI Template.
 * Uses react-hook-form + zod. 409 duplicate-name surfaced inline.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateKpiTemplateMutation } from '@/hooks/useKpiTemplateQueries';
import { toast } from '@/lib/toast';
import { KPI_UNITS, type KpiUnit } from '@/types/kpi';
import type { CreateKpiTemplateBody } from '@/types/kpi-template';

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const createTemplateSchema = z
  .object({
    name: z.string().min(3, 'Name must be at least 3 characters').max(100, 'Name must be at most 100 characters'),
    unit: z.enum([
      'Nos', 'Meters', 'RunningMeters', 'Hours', 'Kilometers',
      'Liters', 'Kilograms', 'Units', 'Trips', 'Custom',
    ] as const),
    customUnitText: z.string().max(50, 'Custom unit must be at most 50 characters').optional(),
    description: z.string().max(500, 'Description must be at most 500 characters').optional(),
  })
  .superRefine((data, ctx) => {
    if (data.unit === 'Custom') {
      if (data.customUnitText === undefined || data.customUnitText.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnitText'],
          message: 'Custom unit text is required when unit is Custom',
        });
      }
    }
  });

type CreateTemplateFormValues = z.infer<typeof createTemplateSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreateKpiTemplateDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateKpiTemplateDialog({
  open,
  onOpenChange,
}: CreateKpiTemplateDialogProps): JSX.Element {
  const mutation = useCreateKpiTemplateMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateTemplateFormValues>({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: { name: '', unit: 'Nos', customUnitText: '', description: '' },
  });

  const watchedUnit = watch('unit');

  function handleClose(): void {
    setServerError(null);
    mutation.reset();
    reset();
    onOpenChange(false);
  }

  function onSubmit(values: CreateTemplateFormValues): void {
    setServerError(null);
    const body: CreateKpiTemplateBody = {
      name: values.name,
      unit: values.unit,
      ...(values.unit === 'Custom' && values.customUnitText
        ? { customUnitText: values.customUnitText }
        : {}),
      ...(values.description && values.description.trim() !== ''
        ? { description: values.description }
        : {}),
    };
    mutation.mutate(body, {
      onSuccess: () => {
        toast.success('KPI template created');
        handleClose();
      },
      onError: (err) => {
        const msg = err.detail ?? err.message;
        setServerError(msg);
        toast.error('Failed to create template', { description: msg });
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="create-kpi-template-dialog">
        <DialogHeader>
          <DialogTitle>Create KPI Template</DialogTitle>
          <DialogDescription>
            Templates can be applied to any Work Order to quickly create KPIs.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              data-testid="template-name-input"
              {...register('name')}
              placeholder="e.g. Standard Throughput"
            />
            {errors.name && (
              <p className="text-sm text-destructive" data-testid="template-name-error">
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Unit */}
          <div className="space-y-1">
            <Label htmlFor="template-unit">Unit</Label>
            <Select
              value={watchedUnit}
              onValueChange={(v) => { setValue('unit', v as KpiUnit); }}
            >
              <SelectTrigger id="template-unit" data-testid="template-unit-trigger">
                <SelectValue placeholder="Select unit…" />
              </SelectTrigger>
              <SelectContent>
                {KPI_UNITS.map((u) => (
                  <SelectItem key={u} value={u} data-testid={`template-unit-option-${u}`}>
                    {u === 'RunningMeters' ? 'Running Meters' : u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom unit text */}
          {watchedUnit === 'Custom' && (
            <div className="space-y-1">
              <Label htmlFor="template-custom-unit">Custom Unit Text</Label>
              <Input
                id="template-custom-unit"
                data-testid="template-custom-unit-input"
                {...register('customUnitText')}
                placeholder="e.g. Pallets"
              />
              {errors.customUnitText && (
                <p className="text-sm text-destructive" data-testid="template-custom-unit-error">
                  {errors.customUnitText.message}
                </p>
              )}
            </div>
          )}

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="template-description">Description (optional)</Label>
            <Textarea
              id="template-description"
              data-testid="template-description-input"
              {...register('description')}
              rows={2}
            />
          </div>

          {/* Server error */}
          {serverError !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="create-template-error"
            >
              {serverError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="create-template-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="create-template-submit"
            >
              {mutation.isPending ? 'Creating…' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
